import { useEffect, useState } from 'react';
import { css, styled } from 'styled-components';

import type { ChatConversation } from '../../../../common';

import { fadeIn } from '../../../../styles/animation';
import FormattedMessage from './Base/formattedMessage';

interface Props {
  content: string;
  isAgent: boolean;
  isLoading: boolean;
  showTextAnimation: boolean;
  padTop?: boolean;
}

export const getMessageContent = (message: ChatConversation): string => {
  return 'question' in message ? message.question : message.answer;
};

const MessageContent: React.FC<Props> = ({
  content,
  isLoading,
  showTextAnimation,
  isAgent,
  padTop = false,
}) => {
  const [chunks, setChunks] = useState<string[]>([]);

  useEffect(() => {
    setChunks((oldChunks) => {
      const diff = content.replace(oldChunks.join(''), '');
      return [...oldChunks, diff];
    });
  }, [content]);

  if (!isLoading) {
    return (
      <MessageDisplay $padTop={padTop} $isAgent={isAgent}>
        <FormattedMessage message={content} />
      </MessageDisplay>
    );
  }

  return (
    <>
      {chunks?.length ? (
        <MessageDisplay $isAgent={isAgent} $padTop={padTop}>
          {chunks
            .filter((chunk) => !!chunk)
            .map((chunk, i) => (
              <Sentence key={i} $showAnimation={showTextAnimation}>
                <FormattedMessage message={chunk} />
              </Sentence>
            ))}
        </MessageDisplay>
      ) : null}
    </>
  );
};

const MessageDisplay = styled.div.attrs<{
  $isAgent: boolean;
  $padTop: boolean;
  $showAnimation?: boolean;
}>((props) => ({
  $isAgent: props.$isAgent ?? false,
  $padTop: props.$padTop ?? false,
  $showAnimation: props.$showAnimation ?? false,
}))`
  color: ${(props) =>
    props.$isAgent ? 'var(--font-color)' : 'var(--color-hint)'};
  margin-bottom: 20px;
  margin-top: ${(props) => (props.$padTop ? '64px' : '0')};
  font-size: var(--conversation-font-size-desktop);
  font-family: var(--conversation-font-family);

  opacity: ${(props) => (props.$showAnimation ? 0 : 1)};
  animation: ${(props) =>
    props.$showAnimation
      ? css`
          ${fadeIn} .6s ease-in-out forwards
        `
      : 'none'};

  @media (max-width: 680px) {
    font-size: var(--conversation-font-size-mobile);
    margin-bottom: 16px;
  }
`;

const Sentence = styled.span.attrs<{ $showAnimation: boolean }>((props) => ({
  $showAnimation: props.$showAnimation ?? false,
}))`
  font-family: var(--conversation-font-family);
  font-size: var(--conversation-font-size-desktop);
  opacity: ${(props) => (props.$showAnimation ? 0 : 1)};
  animation: ${(props) =>
    props.$showAnimation
      ? css`
          ${fadeIn} .6s ease-in-out forwards
        `
      : 'none'};

  @media (max-width: 680px) {
    font-size: var(--conversation-font-size-mobile);
  }
`;

export default MessageContent;
